import { useState } from 'react';
import { CoreV1Event, V1Container, V1Pod } from '@kubernetes/client-node';
import { css, type Theme } from '@emotion/react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { DescribePodData } from 'api/deployment/routes/deploymentDescribePodRoute';
import { Typography } from 'client/components/Typography/Typography';

const Container = ({ container, status }: { container: V1Container; status?: { state?: any; ready?: boolean; restartCount?: number } }) => {
  const state = status?.state as { [key: string]: any };
  const stateKey = state ? Object.keys(state)[0] : '';
  const stateInfo = state?.[stateKey];
  const statusLabel = stateKey ? stateKey.charAt(0).toUpperCase() + stateKey.slice(1) : 'Unknown';
  const reason = stateInfo?.reason || '';
  const message = stateInfo?.message || '';
  const exitCode = status?.state['terminated'] ? status.state['terminated'].exitCode : null;

  return (
    <>
      <table css={infoTableStyle}>
        <tbody>
          <tr>
            <td>Name</td>
            <td>
              <strong>{container.name}</strong>
            </td>
          </tr>
          <tr>
            <td>Status</td>
            <td>
              {status && (
                <>
                  {statusLabel} {reason && `(${reason})`}
                  {status?.restartCount ? ` · Restarts: ${status.restartCount}` : ''}
                  {message ? ` — ${message}` : ''}
                  {exitCode !== null ? ' Exit code: ' + exitCode : ''}
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>Image</td>
            <td>{container.image}</td>
          </tr>
          <tr>
            <td>Image Pull Policy</td>
            <td>{container.imagePullPolicy}</td>
          </tr>
          {container.command && (
            <tr>
              <td>Command</td>
              <td>{container.command?.join(' ') || '-'}</td>
            </tr>
          )}
          <tr>
            <td>Port</td>
            <td>{container.ports?.map((p) => `${p.containerPort}/${p.protocol}`).join(', ') || '-'}</td>
          </tr>
          <tr>
            <td>Environment</td>
            <td>
              {(container.env || []).map((e) => (
                <div>
                  {e.name}: {e.value}
                </div>
              ))}
            </td>
          </tr>
          <tr>
            <td>Volume Mounts</td>
            <td>
              {(container.volumeMounts || []).map((v) => (
                <div>
                  {v.name}: {v.mountPath}
                </div>
              ))}
            </td>
          </tr>
          <tr>
            <td>Resources: CPU</td>
            <td>
              {container.resources?.limits?.cpu || '-'} / Mem {container.resources?.limits?.memory || '-'}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export const PodInfoDialogContents = ({ data }: { data: DescribePodData }) => {
  if (data.status != 'ok') return null;
  const [expand, setExpand] = useState(false);

  const info = JSON.parse(JSON.stringify(data.info)) as V1Pod;
  (info as any).metadata.managedFields = '[REMOVED]';

  const events = JSON.parse(JSON.stringify(data.events)) as CoreV1Event[];
  events.map((event) => {
    (event as any).metadata.managedFields = '[REMOVED]';
  });

  const containers = info.spec?.containers || [];
  const initContainers = info.spec?.initContainers || [];

  return (
    <div css={contentStyle}>
      <Typography variant="body1" className="first" css={sectionStyle}>
        Overview
      </Typography>
      <table css={infoTableStyle}>
        <tbody>
          <tr>
            <td>Name</td>
            <td>
              <strong>{info.metadata?.name}</strong>
            </td>
          </tr>
          <tr>
            <td>Namespace</td>
            <td>{info.metadata?.namespace}</td>
          </tr>
          {/*<tr>
            <td>Node</td>
            <td>{info.spec?.nodeName}</td>
          </tr>*/}
          <tr>
            <td>Status</td>
            <td>{info.status?.phase}</td>
          </tr>
          <tr>
            <td>Pod IP</td>
            <td>{info.status?.podIP}</td>
          </tr>
          {/*<tr>
            <td>Host IP</td>
            <td>{info.status?.hostIP}</td>
          </tr>*/}
          <tr>
            <td>Conditions</td>
            <td>
              {(info.status?.conditions || [])
                .map((c, i) => (c.status == 'True' ? <span key={i}>{c.type}&nbsp;&nbsp;</span> : null))
                .filter((c) => c)}
            </td>
          </tr>
          <tr>
            <td>Start Time</td>
            <td>{info.status?.startTime ? new Date(info.status.startTime).toLocaleString() : '-'}</td>
          </tr>
        </tbody>
      </table>
      <Typography variant="body1" css={sectionStyle}>
        Containers
      </Typography>
      {containers.map((container, i) => {
        const containerStatus = info.status?.containerStatuses?.find((s) => s.name === container.name);
        return (
          <div key={i} css={containerCardStyle}>
            <Container container={container} status={containerStatus} />
          </div>
        );
      })}
      {initContainers.length && (
        <>
          <Typography variant="body1" css={sectionStyle}>
            Init Containers
          </Typography>
          {initContainers.map((container, i) => {
            const containerStatus = info.status?.initContainerStatuses?.find((s) => s.name === container.name);
            return (
              <div key={i} css={containerCardStyle}>
                <Container container={container} status={containerStatus} />
              </div>
            );
          })}
        </>
      )}
      <Typography variant="body1" css={sectionStyle}>
        Events
      </Typography>
      <table css={eventsTableStyle}>
        <thead>
          <tr>
            <th>Message</th>

            {/*<th>Type</th>*/}
            {/*<th>Reason</th>*/}
            <th>Count</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => {
            const timestamp = e.eventTime || e.lastTimestamp || e.firstTimestamp || '';
            const formattedTime = timestamp ? new Date(timestamp).toLocaleString() : '—';
            return (
              <tr key={i}>
                <td>{e.message}</td>

                {/*<td>
                  <span
                    css={css`
                      color: ${e.type === 'Warning' ? '#d32f2f' : '#388e3c'};
                      font-weight: bold;
                    `}
                  >
                    {e.type}
                  </span>
                </td>*/}
                {/*<td>{e.reason}</td>*/}
                <td>{e.count || 1}</td>
                <td>{formattedTime}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Typography variant="body1" onClick={() => setExpand((state) => !state)} css={sectionStyle}>
        Pod Specification (Advanced) e {!expand && <CaretDown weight="bold" />}
        {expand && <CaretUp weight="bold" />}
      </Typography>
      {expand && <pre css={jsonStyle}>{JSON.stringify(info, null, 2)}</pre>}
      {/*
      <Typography variant="body1" css={sectionStyle}>
        Events (RAW)
      </Typography>
      <pre css={jsonStyle}>{JSON.stringify(events, null, 2)}</pre>
      */}
    </div>
  );
};

const contentStyle = css``;

const sectionStyle = css`
  margin-bottom: 10px;
  margin-top: 20px;
  &.first {
    margin-top: 0px;
  }
`;

const jsonStyle = (theme: Theme) => css`
  padding: 10px 10px 10px 10px;
  font-size: 60%;
  background-color: ${theme.color.grey[60]};
  max-height: 300px;
  overflow-y: auto;
`;

const eventsTableStyle = css`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  margin-bottom: 30px;
  font-size: 14px;

  th,
  td {
    padding: 4px 8px;
    text-align: left;
    vertical-align: top;
  }

  th:nth-of-type(2),
  td:nth-of-type(2) {
    width: 120px;
    text-align: center;
  }

  th:nth-of-type(3),
  td:nth-of-type(3) {
    width: 180px;
  }

  thead {
    font-weight: bold;
  }
`;

const infoTableStyle = css`
  td {
    font-size: 14px;
    padding: 2px 0px 2px 0px;
  }
  td:first-of-type {
    width: 180px;
    padding-left: 20px;
  }
`;

const containerCardStyle = (theme: Theme) => css`
  margin: 10px 0px 10px 0px;
  padding-top: 10px;
  padding-bottom: 10px;
  margin-bottom: 10px;
  background-color: ${theme.color.grey[60]};
`;
